"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import type { OutlineItem } from "@studygenie/documents/types";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/locales/client";

interface TableOfContentsFormProps {
  tableOfContents: OutlineItem[];
  onSubmit: (
    selectedSections: { title: string; page: number }[],
  ) => Promise<void>;
  onCancel?: () => void;
  location: "newStudySetDrawer" | "uploadFileModal";
}

// Use composite key for uniqueness: title__page
const getKey = (item: OutlineItem) =>
  `${(item.title ?? "").trim()}__${item.page ?? -1}`;

const FormSchema = z.object({
  items: z
    .array(z.string())
    .min(1, { message: "You have to select at least one item." }),
});

const getAllDescendantKeys = (node: OutlineItem): string[] => {
  const keys: string[] = [getKey(node)];
  if (node.down && node.down.length > 0) {
    for (const child of node.down) keys.push(...getAllDescendantKeys(child));
  }
  return keys;
};

const toggleNode = (
  node: OutlineItem,
  checked: boolean,
  fieldValue: string[],
  onChange: (val: string[]) => void,
) => {
  const keys = new Set(fieldValue);
  const targetKeys = getAllDescendantKeys(node);
  for (const k of targetKeys) {
    if (checked) keys.add(k);
    else keys.delete(k);
  }
  onChange(Array.from(keys));
};

const isIndeterminate = (node: OutlineItem, selected: Set<string>): boolean => {
  const children = node.down ?? [];
  if (children.length === 0) return false;
  let any = false;
  let all = true;
  for (const child of children) {
    const key = getKey(child);
    const childChecked = selected.has(key);
    const childIndeterminate = isIndeterminate(child, selected);
    any = any || childChecked || childIndeterminate;
    all = all && childChecked && !childIndeterminate;
  }
  return any && !all;
};

type FormValues = z.infer<typeof FormSchema>;

const Node = ({
  node,
  level = 0,
  form,
}: {
  node: OutlineItem;
  level?: number;
  form: UseFormReturn<FormValues>;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <FormField
      control={form.control}
      name="items"
      render={({ field }) => {
        const selected = new Set(field.value);
        const key = getKey(node);
        const checked = selected.has(key);
        const indeterminate = isIndeterminate(node, selected) && !checked;
        const hasChildren = (node.down?.length ?? 0) > 0;

        const checkboxId = `checkbox-${key}`;

        return (
          <FormItem className="w-full">
            <div
              className={`flex items-start gap-3 p-2 rounded-md hover:bg-accent transition-colors ${level > 0 ? "ml-6" : ""}`}
              style={{
                paddingLeft: hasChildren ? "8px" : level > 0 ? "32px" : "8px",
              }}
            >
              {hasChildren && (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  className="h-6 w-6 p-0 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}

              <FormControl className="mt-0.5">
                <Checkbox
                  id={checkboxId}
                  checked={checked}
                  disabled={node.page === undefined && !hasChildren}
                  onCheckedChange={(c) =>
                    toggleNode(
                      node,
                      Boolean(c),
                      field.value ?? [],
                      field.onChange,
                    )
                  }
                  {...(indeterminate ? { "data-state": "indeterminate" } : {})}
                  className="shrink-0"
                />
              </FormControl>

              <label
                htmlFor={checkboxId}
                className="flex-1 min-w-0 text-sm cursor-pointer select-none leading-relaxed text-foreground"
              >
                {node.title ?? "Untitled"}
                {node.page !== undefined ? (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    (Page {node.page + 1})
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    (Page not available)
                  </span>
                )}
              </label>
            </div>

            {hasChildren && (
              <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleContent>
                  <div className="space-y-1 mt-1">
                    {node.down!.map((child) => (
                      <Node
                        key={getKey(child)}
                        node={child}
                        level={level + 1}
                        form={form}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </FormItem>
        );
      }}
    />
  );
};

export const TableOfContentsForm = ({
  tableOfContents,
  onSubmit,
  onCancel,
  location,
}: TableOfContentsFormProps) => {
  const t = useI18n();

  // Flatten selection to minimal payload expected by API
  const flattenSelection = (
    items: OutlineItem[],
    selected: Set<string>,
  ): { title: string; page: number }[] => {
    const result: { title: string; page: number }[] = [];
    const dfs = (nodes: OutlineItem[]) => {
      for (const node of nodes) {
        const key = getKey(node);
        if (selected.has(key) && node.title && node.page !== undefined) {
          result.push({ title: node.title, page: node.page });
        }
        if (node.down && node.down.length > 0) dfs(node.down);
      }
    };
    dfs(items);
    return result;
  };

  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const node of tableOfContents) {
      for (const k of getAllDescendantKeys(node)) keys.add(k);
    }
    return Array.from(keys);
  }, [tableOfContents]);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: { items: allKeys },
    mode: "onSubmit",
  });

  const isSubmitting = form.formState.isSubmitting;

  // Keep selection in sync if table of contents changes
  useEffect(() => {
    form.setValue("items", allKeys, { shouldValidate: true });
  }, [allKeys]);

  const selectedItems = form.watch("items");
  const allSelected =
    (selectedItems?.length ?? 0) >= allKeys.length && allKeys.length > 0;

  const handleToggleAll = () => {
    if (allSelected) {
      form.setValue("items", [], { shouldValidate: true });
    } else {
      form.setValue("items", allKeys, { shouldValidate: true });
    }
  };

  const handleSubmit = async (values: z.infer<typeof FormSchema>) => {
    const selected = new Set(values.items);
    const flattened = flattenSelection(tableOfContents, selected);
    await onSubmit(flattened);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div>
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={handleToggleAll}
              className="px-2 h-6"
            >
              {allSelected ? "Unselect all" : "Select all"}
            </Button>
          </div>
          <ScrollArea className="h-[280px]">
            <div className="space-y-1 max-w-md">
              {tableOfContents.length > 0 ? (
                tableOfContents.map((item) => (
                  <Node key={getKey(item)} node={item} form={form} />
                ))
              ) : (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <p className="text-sm">No table of contents available</p>
                </div>
              )}
            </div>
          </ScrollArea>
          <FormMessage />
        </div>
        <div className="flex w-full">
          <Button
            type="submit"
            className="ml-auto"
            disabled={isSubmitting || (selectedItems?.length ?? 0) === 0}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="animate-spin size-4" />{" "}
                <span>
                  {location !== "newStudySetDrawer"
                    ? t("newStudySetDrawer.generatingQuestions")
                    : t("newStudySetDrawer.generatingStudySet")}
                </span>
              </div>
            ) : (
              <span>
                {location !== "newStudySetDrawer"
                  ? t("newStudySetDrawer.generateQuestions")
                  : t("newStudySetDrawer.generateStudySet")}
              </span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
